import { Card, Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import 'filepond/dist/filepond.min.css';
import { useEffect, useState } from 'react';
import FilesTable, { SignableDocument } from './components/FilesTable';
import { useAppDispatch } from '../../../store/store';
import { fetchUploads } from '../../../store/slices/uploads';
// eslint-disable-next-line max-len
import { InfoOutlined } from '@mui/icons-material';
import { listings, UploadsType, OfferStatus, UserType } from '@prisma/client';
import { useAuth } from '../../../contexts/AuthContext';
import FileUpload from '../../../components/FileUpload/FileUpload';
import { useParams } from 'react-router-dom';
import useIsMobile from '../../../hooks/useIsMobile';
// eslint-disable-next-line max-len
import { CustomTooltip } from '../../../components/CustomTooltip/CustomTooltip';
import offerDocumentsService from '../../../api/services/offerDocuments';
// eslint-disable-next-line max-len

interface Props {
  listing: listings;
  offerId?: string;
  token?: string;
  offerStatus?: OfferStatus;
  // Buyers sign their purchase agreement from the property row workflow, so
  // the documents list hides the sign action for them.
  allowSigning?: boolean;
  readOnly?: boolean;
}

const BuyerDetailFiles: React.FC<Props> = ({
  listing,
  offerId,
  token,
  offerStatus,
  allowSigning = true,
  readOnly = false,
}) => {
  const { currentUser } = useAuth();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const [signableDocs, setSignableDocs] = useState<SignableDocument[]>([]);

  const canSeeSignables = !token && !!currentUser && (
    currentUser.type === UserType.ADMIN ||
    currentUser.id === listing.userId ||
    currentUser.id === id
  );

  const refreshUploads = () => {
    dispatch(fetchUploads({ listingId: listing.id, token }));
  };

  const refreshSignables = async () => {
    if (!offerId || !canSeeSignables) {
      setSignableDocs([]);
      return;
    }
    try {
      const docs = await offerDocumentsService.getByOfferId(offerId);
      setSignableDocs(docs || []);
    } catch (error) {
      console.error('Error fetching signable documents:', error);
    }
  };

  useEffect(() => {
    refreshUploads();
    refreshSignables();
  }, [offerId, canSeeSignables]);

  // Leaving in case we want to do something similar in the future
  // const handleFileUploadResponse = (responseText: string) => {
  //   const response = JSON.parse(responseText);
  //   const upload = response[0].uploadResult;

  //   if (upload.docType === DocType.INSPECTION_ADDENDUM) {
  //     NiceModal.show(AddendumResponseDateModal, {
  //       addendum: upload.addendum,
  //     });
  //   }
  // };

  const InfoComponent = () =>
    isMobile ? (
      <CustomTooltip
        // eslint-disable-next-line max-len
        title="You may upload any documents that are relevant to the transaction below. This includes inspection reports, repair receipts, pre-approval, and any other documents."
        placement="top"
      >
        <InfoOutlined />
      </CustomTooltip>
    ) : (
      <>
        <InfoOutlined />
        <Typography variant="body2">
          You may upload any documents that are relevant to the transaction
          below. This includes inspection reports, repair receipts,
          pre-approval, and any other documents.
        </Typography>
      </>
    );

  return (
    <>
      <Grid2 container spacing={1}>
        <Grid2 xs={12}>
          <Card elevation={1}>
            <Stack direction="column" spacing={2} p={2}>
              <Grid2 xs={12}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="h6"
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    Offer documents
                  </Typography>
                  <InfoComponent />
                </Stack>
              </Grid2>
              {!readOnly &&
                (currentUser?.id ||
                  (token && offerStatus === OfferStatus.ACCEPTED)) && (
                <FileUpload
                  listingId={listing.id}
                  userId={currentUser?.id}
                  onUpload={refreshUploads}
                  docType={UploadsType.TRANSACTION_DOC}
                  analyze={true}
                  buyerId={id}
                  token={token}
                />
              )}
            </Stack>
            {(id || token) && (
              <FilesTable
                listingId={listing.id}
                buyerId={id}
                offerId={offerId}
                token={token}
                signableDocuments={signableDocs}
                offerStatus={offerStatus}
                allowSigning={allowSigning}
              />
            )}
          </Card>
        </Grid2>
      </Grid2>
    </>
  );
};

export default BuyerDetailFiles;
